import { ScrollArea } from "~/components/ui/scroll-area";
import { StatusPill, type StatusPillTone } from "~/components/status-pill";
import { formatLanguageTime } from "~/lib/language-lens/intl";
import type { ExecutionToastStatus } from "../copy/execution-toast-copy";

/**
 * Structural event shape covering both timeline sources: tRPC
 * `v2.executions.detail` events (hydration/fallback) and run-DO live frames
 * (`useExecutionOperationLive`).
 */
export interface ToastTimelineEvent {
  readonly createdAt: string;
  readonly detail?: string | null;
  readonly id: string;
  readonly kind: string;
  readonly sequence: number;
  readonly status?:
    | "pending"
    | "active"
    | "waiting"
    | "blocked"
    | "completed"
    | "failed"
    | "info"
    | null;
  readonly title: string;
}

function toneForEventStatus(
  status: ToastTimelineEvent["status"],
): StatusPillTone {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "destructive";
    case "active":
      return "info";
    case "waiting":
    case "blocked":
      return "pending";
    default:
      return "default";
  }
}

function formatEventTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return formatLanguageTime(date, { hour: "numeric", minute: "2-digit" });
}

/**
 * Collapse runs of near-identical wait events (deposit.indexer_wait repeats
 * every ~15 polls, execution.action_recorded per signal attempt) down to the
 * latest of each consecutive kind run, keeping the timeline readable.
 */
export function groupTimelineEvents(
  events: readonly ToastTimelineEvent[],
): ToastTimelineEvent[] {
  const grouped: ToastTimelineEvent[] = [];

  for (const event of events) {
    const previous = grouped[grouped.length - 1];
    if (previous && previous.kind === event.kind) {
      grouped[grouped.length - 1] = event;
    } else {
      grouped.push(event);
    }
  }

  return grouped;
}

export function ExecutionToastTimeline({
  events,
}: {
  events: readonly ToastTimelineEvent[];
}) {
  const grouped = groupTimelineEvents(events);

  return (
    <ScrollArea className="max-h-64 pr-1">
      <div className="space-y-2">
      {grouped.map((event) => (
        <div className="flex gap-3" key={`${event.id}:${event.sequence}`}>
          <div className="flex w-12 shrink-0 justify-end pt-1">
            <span className="text-caption tabular-nums text-muted-foreground">
              {formatEventTime(event.createdAt)}
            </span>
          </div>
          <div className="min-w-0 flex-1 rounded-2xl border border-border/70 bg-muted/30 px-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 truncate text-meta font-medium text-foreground">
                {event.title}
              </p>
              {event.status ? (
                <StatusPill tone={toneForEventStatus(event.status)}>
                  {event.status}
                </StatusPill>
              ) : null}
            </div>
            {event.detail ? (
              <p className="mt-0.5 text-caption text-muted-foreground">
                {event.detail}
              </p>
            ) : null}
          </div>
        </div>
      ))}
      </div>
    </ScrollArea>
  );
}

interface FallbackStep {
  id: string;
  label: string;
  status: "completed" | "active" | "pending" | "failed";
}

/** v1's three-step fallback while the persisted timeline has not loaded. */
export function buildFallbackTimelineSteps(
  status: ExecutionToastStatus,
): FallbackStep[] {
  if (status === "error" || status === "cancelled") {
    return [
      { id: "submitted", label: "Submitted", status: "completed" },
      { id: "failed", label: "Failed", status: "failed" },
      { id: "complete", label: "Complete", status: "pending" },
    ];
  }

  if (status === "success") {
    return [
      { id: "submitted", label: "Submitted", status: "completed" },
      { id: "confirming", label: "Confirming", status: "completed" },
      { id: "complete", label: "Complete", status: "completed" },
    ];
  }

  return [
    { id: "submitted", label: "Submitted", status: "completed" },
    { id: "confirming", label: "Confirming", status: "active" },
    { id: "complete", label: "Complete", status: "pending" },
  ];
}

export function FallbackTimeline({
  status,
}: {
  status: ExecutionToastStatus;
}) {
  const steps = buildFallbackTimelineSteps(status);

  return (
    <div className="space-y-2">
      {steps.map((step) => (
        <div className="flex items-center justify-between gap-3" key={step.id}>
          <span
            className={
              step.status === "pending"
                ? "text-meta text-muted-foreground"
                : "text-meta font-medium text-foreground"
            }
          >
            {step.label}
          </span>
          <StatusPill
            tone={
              step.status === "completed"
                ? "success"
                : step.status === "failed"
                  ? "destructive"
                  : step.status === "active"
                    ? "info"
                    : "default"
            }
          >
            {step.status}
          </StatusPill>
        </div>
      ))}
    </div>
  );
}
